<template>
    <form class="cobalt-task-form" ref="componentWrapper" @clickOutside="onClickOutside" @submit.prevent="" :class="class">
        <div class="np-input-group" :class="{'active' : isFocused}">
            <textarea 
                ref="titleInput" 
                class="np-text-input" 
                :placeholder="placeholder" 
                :disabled="isLoading"  
                :value="value"
                :rows="rows"
                :style="textareaStyle"
                @focusin="focusIn"
                @input="adjustTextareaHeight"
                @keydown.enter.prevent="save" 
                @keydown.esc="cancel">
            </textarea>
            <div class="actions">
                <button type="button" class="np-btn np-btn-link np-mr-1" @click="cancel">
                    <svg><use href="@/assets/images/icons/cancel2.svg#icon"></use></svg>
                </button>
                <button type="button" class="np-btn np-btn-link" @click="save" :disabled="isLoading">
                    <svg><use href="@/assets/images/icons/checked3.svg#icon"></use></svg>
                </button>                                                                                                
            </div>
        </div>
    </form>
</template>

<script>
export default {
    props: {
        placeholder: {
            type: String,
            required: false
        },
        class: {
            type: String,
            required: false            
        },
        value: {
            type: String,
            required: false
        },
        ignoreOutsideClick: {
            type: Boolean,
            default: false
        },
        rows: {
            type: Number,
            default: 1
        },
        multiline: {
            type: Boolean,
            default: false
        },
        minHeight: {
            type: Number,
            default: null
        },
        maxHeight: {
            type: Number,
            default: null
        }
    },    
    data() {
        return {
            componentWrapper: null,
            isFocused: false,
            isLoading: false,
            isVisible: false
        }
    },
    computed: {
        textareaStyle() {
            return {
                overflow: this.multiline ? 'auto' : 'hidden',
                resize: this.multiline ? 'vertical' : 'none',
                minHeight: this.minHeight ? `${this.minHeight}px` : null,
                maxHeight: this.maxHeight ? `${this.maxHeight}px` : null
            }
        }
    },
    mounted() {
        this.componentWrapper = this.$refs.componentWrapper;
        document.addEventListener('mouseup', this.handleClickOutside);
        
        this.initVisibilityObserver();
    },
    beforeDestroy() {
        document.removeEventListener('mouseup', this.handleClickOutside);
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        initVisibilityObserver() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    this.isVisible = entry.isIntersecting;
                    if (this.isVisible) {
                        this.$nextTick(this.adjustTextareaHeight);
                    }
                });
            }, { threshold: 0.1 });
            
            if (this.$refs.titleInput) {
                this.observer.observe(this.$refs.titleInput);
            }
        },
        adjustTextareaHeight() {
            const textarea = this.$refs.titleInput;
            if (!textarea) return;

            const originalVisibility = textarea.style.visibility;
            const originalPosition = textarea.style.position;
            const originalDisplay = textarea.style.display;
            
            textarea.style.visibility = 'hidden';
            textarea.style.position = 'absolute';
            textarea.style.display = 'block';

            textarea.style.height = 'auto';
            let newHeight = textarea.scrollHeight;

            if (this.minHeight) {
                newHeight = Math.max(newHeight, this.minHeight);
            }
            if (this.maxHeight) {
                newHeight = Math.min(newHeight, this.maxHeight);
            }

            textarea.style.visibility = originalVisibility;
            textarea.style.position = originalPosition;
            textarea.style.display = originalDisplay;
            textarea.style.height = `${newHeight}px`;
        },
        focusIn() {
            const input = this.$refs.titleInput;
            input.focus();
            this.isFocused = true;
            this.adjustTextareaHeight();
        },   
        cancel() {
            const input = this.$refs.titleInput;
            if (input) {
                input.value = '';
                input.blur();
                this.isFocused = false;
                this.$emit('onCancel');
            }
        },
        async save() {
            if (!this.$refs.titleInput.value) return;

            this.isLoading = true;
            await this.$emit('onSubmit', this.$refs.titleInput.value);
            this.isLoading = false;

            this.cancel();            
        },
        handleClickOutside(event) {
            if (!this.ignoreOutsideClick &&
                this.componentWrapper &&
                !this.componentWrapper.contains(event.target) &&
                event.target !== this.$refs.titleInput
            ) {
                this.cancel();
            }
        }        
    },
    watch: {
        value(newVal) {
            this.$nextTick(this.adjustTextareaHeight);
        },
        isFocused(newVal) {
            if (newVal) {
                this.$nextTick(this.adjustTextareaHeight);
            }
        }
    }
}
</script>